import {
  Badge,
  Box,
  HStack,
  Icon,
  IconButton,
  Text,
  VStack,
} from '@chakra-ui/react';
import { DeleteIcon } from '@diamond/shared/icons';
import { CloudUpload, FilePresentOutlined } from '@mui/icons-material';
import { useCallback, useState } from 'react';
import type { Accept, FileRejection } from 'react-dropzone';
import { useDropzone } from 'react-dropzone';

interface ProductLimitationDropzoneProps {
  file: File | null;
  accept?: Accept;
  maxSizeMb?: number;
  isDisabled?: boolean;
  isUploading?: boolean;
  onFileChange: (file: File | null) => void;
  onFileRejected: (message: string) => void;
}

const DEFAULT_ACCEPT: Accept = {
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
    '.xlsx',
  ],
  'application/vnd.ms-excel': ['.xls', '.xlsx'],
};

export function ProductLimitationDropzone({
  file,
  accept = DEFAULT_ACCEPT,
  maxSizeMb = 2,
  isDisabled = false,
  isUploading = false,
  onFileChange,
  onFileRejected,
}: ProductLimitationDropzoneProps) {
  const [isDragActive, setIsDragActive] = useState(false);

  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      if (rejectedFiles.length > 0) {
        const rejection = rejectedFiles[0];

        if (rejection.errors.some((error) => error.code === 'file-too-large')) {
          onFileRejected(`File terlalu besar. Maksimal ${maxSizeMb}MB`);
          return;
        }

        if (
          rejection.errors.some((error) => error.code === 'file-invalid-type')
        ) {
          onFileRejected(
            'Format file tidak didukung. Gunakan file Excel (.xlsx)'
          );
          return;
        }

        onFileRejected('File tidak valid. Silakan cek kembali file Anda.');
        return;
      }

      if (acceptedFiles.length > 0) {
        onFileChange(acceptedFiles[0]);
      }
    },
    [maxSizeMb, onFileChange, onFileRejected]
  );

  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    accept,
    maxFiles: 1,
    maxSize: maxSizeMb * 1024 * 1024,
    noClick: true,
    onDragEnter: () => setIsDragActive(true),
    onDragLeave: () => setIsDragActive(false),
    onDropAccepted: () => setIsDragActive(false),
    onDropRejected: () => setIsDragActive(false),
  });

  const isInteractionDisabled = isUploading || isDisabled;

  if (file) {
    return (
      <HStack
        spacing={3}
        align="start"
        border="1px"
        borderColor="green.200"
        borderRadius="md"
        p={3}
        bg="green.50"
        w="full"
      >
        <Box
          border="1px solid"
          borderColor="green.200"
          borderRadius="md"
          p={2}
          bg="white"
          minW="40px"
          h="40px"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <FilePresentOutlined fontSize="small" />
        </Box>

        <VStack align="start" spacing={1} flex={1}>
          <HStack spacing={2}>
            <Text fontSize="sm" fontWeight="medium" color="gray.800">
              {file.name}
            </Text>
            <Badge colorScheme="green" borderRadius="md" px={2}>
              File Ready
            </Badge>
          </HStack>
          <Text fontSize="xs" color="gray.600">
            {(file.size / 1024).toFixed(1)} KB
          </Text>
          <Text fontSize="xs" color="green.700">
            File Ready untuk proses upload.
          </Text>
        </VStack>

        <IconButton
          aria-label="Hapus file"
          icon={<DeleteIcon />}
          size="sm"
          variant="ghost"
          colorScheme="red"
          onClick={() => onFileChange(null)}
          isDisabled={isInteractionDisabled}
        />
      </HStack>
    );
  }

  return (
    <Box
      {...getRootProps()}
      border="2px dashed"
      borderColor={isDragActive ? 'blue.400' : 'gray.300'}
      borderRadius="lg"
      p={6}
      bg={isDragActive ? 'blue.50' : 'gray.50'}
      textAlign="center"
      cursor={isInteractionDisabled ? 'not-allowed' : 'pointer'}
      opacity={isInteractionDisabled ? 0.6 : 1}
      onClick={isInteractionDisabled ? undefined : open}
      pointerEvents={isInteractionDisabled ? 'none' : 'auto'}
    >
      <input {...getInputProps()} disabled={isInteractionDisabled} />
      <VStack spacing={3}>
        <Box
          borderRadius="full"
          bg={isDragActive ? 'blue.100' : 'white'}
          border="1px"
          borderColor={isDragActive ? 'blue.300' : 'gray.200'}
          p={3}
        >
          <Icon as={CloudUpload} boxSize={6} color="blue.500" />
        </Box>
        <Text fontSize="sm" color="gray.700" fontWeight="medium">
          {isUploading ? 'Uploading file...' : 'Drag & drop file .xlsx di sini'}
        </Text>
        <Text fontSize="xs" color="gray.500">
          {isUploading
            ? 'Mohon tunggu hingga proses upload selesai'
            : 'Atau klik area ini untuk memilih file dari perangkat Anda'}
        </Text>
        <HStack spacing={2}>
          <Badge colorScheme="purple" borderRadius="md" px={2}>
            .xlsx
          </Badge>
          <Badge colorScheme="orange" borderRadius="md" px={2}>
            Maks {maxSizeMb}MB
          </Badge>
        </HStack>
      </VStack>
    </Box>
  );
}
